'use client';

import { Inbox, MessageSquare } from 'lucide-react';
import { useSupportTickets } from '../hooks/use-support';
import type { SupportTicket } from '../services/support-api';
import { formatRelativeTime, statusLabel, statusToneClasses } from '../lib/format';

interface SupportTicketsListProps {
  onSelectTicket: (id: string) => void;
  limit?: number;
  showEmptyHint?: boolean;
}

export function SupportTicketsList({
  onSelectTicket,
  limit = 5,
  showEmptyHint = true,
}: SupportTicketsListProps) {
  const { data, isLoading } = useSupportTickets(undefined, limit);
  const tickets = data?.data ?? [];

  if (isLoading) {
    return (
      <div className="space-y-1.5" aria-busy="true">
        {Array.from({ length: 2 }).map((_, i) => (
          <div
            key={i}
            className="h-[58px] animate-pulse rounded-xl bg-neutral-100 dark:bg-neutral-900"
          />
        ))}
      </div>
    );
  }

  if (tickets.length === 0) {
    if (!showEmptyHint) return null;
    return (
      <div className="flex items-center gap-3 rounded-xl border border-dashed border-neutral-200 px-3.5 py-3 text-[0.8125rem] text-neutral-500 dark:border-neutral-800 dark:text-neutral-500">
        <Inbox className="h-4 w-4" strokeWidth={1.75} />
        Nu ai încă tichete deschise.
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {tickets.map((ticket) => (
        <TicketRow
          key={ticket.id}
          ticket={ticket}
          onClick={() => onSelectTicket(ticket.id)}
        />
      ))}
    </div>
  );
}

interface TicketRowProps {
  ticket: SupportTicket;
  onClick: () => void;
}

function TicketRow({ ticket, onClick }: TicketRowProps) {
  const lastMsg = ticket.messages?.[0];
  return (
    <button
      type="button"
      onClick={onClick}
      className="group block w-full rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 text-left transition-all hover:border-amber-300 hover:bg-amber-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-amber-700/50 dark:hover:bg-amber-950/20"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[0.8125rem] font-medium text-neutral-900 dark:text-neutral-100">
            {ticket.subject}
          </div>
          {lastMsg && (
            <div className="mt-0.5 flex items-center gap-1.5 text-[0.6875rem] text-neutral-500 dark:text-neutral-500">
              <MessageSquare className="h-3 w-3" strokeWidth={1.75} />
              <span className="truncate">
                {lastMsg.authorType === 'agent' ? 'Echipa SoftFactura: ' : ''}
                {lastMsg.body.slice(0, 60)}
              </span>
            </div>
          )}
        </div>
        <span
          className={`shrink-0 rounded-full px-1.5 py-0.5 text-[0.625rem] font-medium ${statusToneClasses(
            ticket.status,
          )}`}
        >
          {statusLabel(ticket.status)}
        </span>
      </div>
      <div className="mt-1 text-[0.625rem] uppercase tracking-wider text-neutral-400 dark:text-neutral-600">
        {formatRelativeTime(ticket.updatedAt)}
      </div>
    </button>
  );
}
